import { getAircraftType } from './AircraftCatalog.js';
import { clamp, damp, normalizeAngle } from './math.js';

const BLUE_STARTS = [
  { x: 0, z: 650 }, { x: -24, z: 690 }, { x: 24, z: 690 },
  { x: -48, z: 735 }, { x: 48, z: 735 }
];
const RED_STARTS = [
  { x: 0, z: -13200 }, { x: -24, z: -13240 }, { x: 24, z: -13240 },
  { x: -48, z: -13285 }, { x: 48, z: -13285 }
];

export const BATTLE_LIMITS = Object.freeze({
  gunRange: 920,
  missileRange: 2200,
  gunCone: 0.2,
  missileCone: 0.48,
  takeoffSpeed: 49,
  battleFloor: 70,
  battleCeiling: 950
});

function createAgent(team, index, typeId, isPlayer = false) {
  const type = getAircraftType(typeId);
  const start = team === 'blue' ? BLUE_STARTS[index] : RED_STARTS[index];
  return {
    id: `${team}-${index + 1}`,
    callSign: `${team === 'blue' ? 'BLUE' : 'RED'} ${index + 1}`,
    team,
    index,
    isPlayer,
    typeId: type.id,
    position: { x: start.x, y: team === 'blue' ? 4.6 : 16.6, z: start.z },
    heading: team === 'blue' ? 0 : Math.PI,
    pitch: 0,
    roll: 0,
    speed: 0,
    throttle: 0.08,
    verticalSpeed: 0,
    grounded: true,
    alive: true,
    health: type.health,
    maxHealth: type.health,
    missiles: type.missiles,
    gunCooldown: 0,
    missileCooldown: 0,
    targetId: null,
    kills: 0,
    lastHitBy: null,
    launchDelay: index * 0.18,
    tacticalMode: 'launch'
  };
}

export function createBattleState({ blueType = 'falcon', redType = 'viper' } = {}) {
  const agents = [];
  for (let index = 0; index < 5; index += 1) {
    agents.push(createAgent('blue', index, blueType, index === 0));
    agents.push(createAgent('red', index, redType));
  }
  return {
    phase: 'preflight',
    elapsed: 0,
    agents,
    blueType: getAircraftType(blueType).id,
    redType: getAircraftType(redType).id,
    playerId: 'blue-1',
    playerTargetId: null,
    score: 0,
    winner: null,
    events: [],
    shotsFired: 0,
    hits: 0
  };
}

export function startBattle(state) {
  state.phase = 'launch';
  state.elapsed = 0;
  state.winner = null;
  state.events.length = 0;
  return state;
}

export function livingAgents(state, team) {
  return state.agents.filter((agent) => agent.alive && (!team || agent.team === team));
}

export function getAgent(state, id) {
  return state.agents.find((agent) => agent.id === id) ?? null;
}

function distanceBetween(a, b) {
  return Math.hypot(
    a.position.x - b.position.x,
    a.position.y - b.position.y,
    a.position.z - b.position.z
  );
}

function desiredHeading(from, to) {
  return Math.atan2(
    -(to.position.x - from.position.x),
    -(to.position.z - from.position.z)
  );
}

function targetAngle(agent, target) {
  return Math.abs(normalizeAngle(desiredHeading(agent, target) - agent.heading));
}

export function chooseTarget(state, agent) {
  const enemies = livingAgents(state, agent.team === 'blue' ? 'red' : 'blue');
  if (!enemies.length) return null;
  return enemies.reduce((best, candidate) => {
    if (!best) return candidate;
    return distanceBetween(agent, candidate) < distanceBetween(agent, best)
      ? candidate
      : best;
  }, null);
}

function deterministicHit(agent, target, elapsed, weapon) {
  const seed =
    (agent.index + 3) * 41 +
    (target.index + 7) * 67 +
    Math.floor(elapsed * (weapon === 'missile' ? 2 : 8));
  const wave = (Math.sin(seed * 12.9898) + 1) * 0.5;
  return wave > (weapon === 'missile' ? 0.16 : 0.42);
}

export function applyDamage(state, target, amount, attacker, weapon = 'gun') {
  if (!target?.alive || amount <= 0) return false;
  target.health = Math.max(0, target.health - amount);
  target.lastHitBy = attacker?.id ?? null;
  state.hits += 1;
  state.events.push({
    type: 'hit',
    weapon,
    attackerId: attacker?.id ?? null,
    targetId: target.id,
    amount,
    position: { ...target.position }
  });
  if (target.health > 0) return false;
  target.alive = false;
  target.tacticalMode = 'destroyed';
  if (attacker) attacker.kills += 1;
  if (attacker?.team === 'blue') state.score += target.isPlayer ? 0 : 1000;
  state.events.push({
    type: 'destroyed',
    attackerId: attacker?.id ?? null,
    targetId: target.id,
    position: { ...target.position }
  });
  return true;
}

function forwardShotEndpoint(agent, distance) {
  const horizontal = Math.cos(agent.pitch);
  return {
    x: agent.position.x - Math.sin(agent.heading) * horizontal * distance,
    y: agent.position.y + Math.sin(agent.pitch) * distance,
    z: agent.position.z - Math.cos(agent.heading) * horizontal * distance
  };
}

function attemptShot(state, agent, target, weapon, options = {}) {
  if (!agent?.alive) return false;
  const { forceHit = false, emitWithoutTarget = false } = options;
  const type = getAircraftType(agent.typeId);
  const isMissile = weapon === 'missile';
  const range = isMissile ? BATTLE_LIMITS.missileRange : BATTLE_LIMITS.gunRange;
  const cone = isMissile ? BATTLE_LIMITS.missileCone : BATTLE_LIMITS.gunCone;
  const targetIsValid = Boolean(
    target?.alive &&
    distanceBetween(agent, target) <= range &&
    targetAngle(agent, target) <= cone
  );
  if (!targetIsValid && !emitWithoutTarget) return false;
  if (isMissile) {
    if (agent.missileCooldown > 0 || agent.missiles <= 0) return false;
    agent.missiles -= 1;
    agent.missileCooldown = 4.8;
  } else {
    if (agent.gunCooldown > 0) return false;
    agent.gunCooldown = agent.isPlayer ? 0.1 : 0.32;
  }
  state.shotsFired += 1;
  const to = targetIsValid ? { ...target.position } : forwardShotEndpoint(agent, range);
  state.events.push({
    type: 'shot', weapon, attackerId: agent.id,
    targetId: targetIsValid ? target.id : null,
    locked: targetIsValid,
    from: { ...agent.position }, to
  });
  if (targetIsValid && (forceHit || deterministicHit(agent, target, state.elapsed, weapon))) {
    const damage = isMissile ? type.missileDamage : type.gunDamage;
    applyDamage(state, target, damage, agent, weapon);
  }
  return true;
}

function updateLaunch(agent, dt, groundHeight) {
  const type = getAircraftType(agent.typeId);
  agent.throttle = damp(agent.throttle, 1, 1.7, dt);
  agent.speed = clamp(
    agent.speed + (32 * type.acceleration - agent.speed * 0.052) * dt,
    0,
    type.maxSpeed
  );
  agent.position.y = groundHeight + 4.6;
  agent.position.x += -Math.sin(agent.heading) * agent.speed * dt;
  agent.position.z += -Math.cos(agent.heading) * agent.speed * dt;
  if (agent.speed >= BATTLE_LIMITS.takeoffSpeed && agent.throttle > 0.72) {
    agent.grounded = false;
    agent.pitch = 0.16;
    agent.verticalSpeed = 7.5 * type.climbRate;
    agent.tacticalMode = 'climb';
  }
}

function updateAirborneAI(state, agent, target, dt, groundHeight) {
  const type = getAircraftType(agent.typeId);
  const desired = target ? desiredHeading(agent, target) : agent.heading;
  const headingError = normalizeAngle(desired - agent.heading);
  const evade = agent.health < agent.maxHealth * 0.35
    ? Math.sin(state.elapsed * 1.7 + agent.index) * 0.42
    : 0;
  const rollTarget = clamp(headingError * 1.65 + evade, -1.02, 1.02);
  agent.roll = damp(agent.roll, rollTarget, 3.1 * type.turnRate, dt);
  agent.heading = normalizeAngle(
    agent.heading + Math.sin(agent.roll) * 0.5 * type.turnRate * dt
  );

  const minimumAltitude = groundHeight + BATTLE_LIMITS.battleFloor;
  const targetAltitude = target
    ? clamp(target.position.y + Math.sin(state.elapsed + agent.index) * 28, minimumAltitude, BATTLE_LIMITS.battleCeiling)
    : Math.max(minimumAltitude, 260);
  const altitudeError = targetAltitude - agent.position.y;
  agent.pitch = damp(agent.pitch, clamp(altitudeError / 340, -0.3, 0.32), 1.9, dt);
  agent.verticalSpeed = damp(
    agent.verticalSpeed,
    Math.sin(agent.pitch) * agent.speed * 0.72,
    1.4,
    dt
  );
  agent.speed = damp(agent.speed, type.cruiseSpeed, 0.7, dt);
  agent.position.x += -Math.sin(agent.heading) * agent.speed * dt;
  agent.position.z += -Math.cos(agent.heading) * agent.speed * dt;
  agent.position.y = clamp(
    agent.position.y + agent.verticalSpeed * dt,
    minimumAltitude,
    BATTLE_LIMITS.battleCeiling
  );
  agent.tacticalMode = target ? 'engage' : 'patrol';

  if (target) {
    const distance = distanceBetween(agent, target);
    if (distance < 1650 && targetAngle(agent, target) < 0.38 && agent.missiles > 0) {
      attemptShot(state, agent, target, 'missile');
    } else if (distance < BATTLE_LIMITS.gunRange) {
      attemptShot(state, agent, target, 'gun');
    }
  }
}

export function syncPlayerAgent(state, flight) {
  const player = getAgent(state, state.playerId);
  if (!player) return null;
  player.position = { ...flight.position };
  player.heading = flight.heading;
  player.pitch = flight.pitch;
  player.roll = flight.roll;
  player.speed = flight.speed;
  player.throttle = flight.throttle;
  player.verticalSpeed = flight.verticalSpeed;
  player.grounded = flight.grounded;
  if (flight.crashed && player.alive) applyDamage(state, player, player.health, null, 'crash');
  return player;
}

export function acquirePlayerTarget(state) {
  const player = getAgent(state, state.playerId);
  if (!player?.alive) return null;
  const candidates = livingAgents(state, 'red')
    .map((target) => ({
      target,
      distance: distanceBetween(player, target),
      angle: targetAngle(player, target)
    }))
    .filter(({ distance, angle }) => distance <= BATTLE_LIMITS.missileRange && angle < 0.7)
    .sort((a, b) => a.angle * 800 + a.distance - (b.angle * 800 + b.distance));
  state.playerTargetId = candidates[0]?.target.id ?? null;
  player.targetId = state.playerTargetId;
  return candidates[0]?.target ?? null;
}

export function firePlayerWeapon(state, weapon) {
  const player = getAgent(state, state.playerId);
  const target = getAgent(state, state.playerTargetId) ?? acquirePlayerTarget(state);
  return attemptShot(state, player, target, weapon, {
    forceHit: true,
    emitWithoutTarget: true
  });
}

function finishIfNeeded(state) {
  const blue = livingAgents(state, 'blue').length;
  const red = livingAgents(state, 'red').length;
  if (blue === 0 || red === 0) {
    state.winner = red === 0 ? 'blue' : 'red';
    state.phase = 'finished';
    state.events.push({ type: 'battle-finished', winner: state.winner });
    return true;
  }
  if (state.phase === 'launch' && state.agents.every((agent) => !agent.alive || !agent.grounded)) {
    state.phase = 'combat';
    state.events.push({ type: 'merge' });
  }
  return false;
}

export function updateBattle(state, dt, environment = {}) {
  if (state.phase === 'preflight' || state.phase === 'finished') return [];
  const step = clamp(dt, 0, 0.05);
  state.elapsed += step;
  state.events.length = 0;
  for (const agent of state.agents) {
    if (!agent.alive || agent.isPlayer) continue;
    agent.gunCooldown = Math.max(0, agent.gunCooldown - step);
    agent.missileCooldown = Math.max(0, agent.missileCooldown - step);
    if (state.elapsed < agent.launchDelay) continue;
    const groundHeight = environment.groundHeight?.(agent.position.x, agent.position.z)
      ?? (agent.team === 'red' ? 12 : 0);
    if (agent.grounded) {
      updateLaunch(agent, step, groundHeight);
      continue;
    }
    const target = chooseTarget(state, agent);
    agent.targetId = target?.id ?? null;
    updateAirborneAI(state, agent, target, step, groundHeight);
  }
  const player = getAgent(state, state.playerId);
  if (player) {
    player.gunCooldown = Math.max(0, player.gunCooldown - step);
    player.missileCooldown = Math.max(0, player.missileCooldown - step);
    acquirePlayerTarget(state);
  }
  finishIfNeeded(state);
  return [...state.events];
}
